import { useCallback, useEffect, useRef, useState } from 'react';
import { useInviteCodeJoin, type CatalogCrucible } from '../../hooks';
import { CrucibleCard, CreateCruciblePanel, InviteCodeForm, type CrucibleCreateDraft } from './components';
import styles from './CruciblesMainPanel.module.scss';
import { Button, Panel, Popover } from '@sorrem-client/ui/components';
import { useCharacterCommissions } from '@sorrem-client/ui/hooks';
import { useCharacterHomeCharacter } from '../../components/CharacterHomeItemTransfer';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface CruciblesMainPanelProps {
    crucibles: readonly CatalogCrucible[];
    /** Whether more open crucibles can be loaded under the list. */
    hasMoreCrucibles?: boolean;
    isLoadingMoreCrucibles?: boolean;
    onShowMoreCrucibles?: () => void;
    selectedCrucibleId: string | null;
    onSelectCrucible: (crucibleId: string) => void;
    /** What the open create form would make, or null once it closes (#224). */
    onCreateDraftChange?: (draft: CrucibleCreateDraft | null) => void;
}

/** Crucibles' center-panel list. Selection is lifted to `CharacterHome.tsx` since the side-panel `CrucibleInfoPanel` needs to show the same selection. */
export function CruciblesMainPanel({
    crucibles,
    hasMoreCrucibles = false,
    isLoadingMoreCrucibles = false,
    onShowMoreCrucibles,
    selectedCrucibleId,
    onSelectCrucible,
    onCreateDraftChange,
}: CruciblesMainPanelProps): JSX.Element {
    const [isCreateFormShown, setIsCreateFormShown] = useState(false);
    const characterReadiness = useCharacterHomeCharacter()?.readiness ?? null;
    const { isSaturationLimited } = useCharacterCommissions();
    const joinViaCodeRef = useRef<HTMLSpanElement>(null);
    const [isJoinViaCodeShown, setIsJoinViaCodeShown] = useState(false);
    const closeJoinViaCode = useCallback((): void => setIsJoinViaCodeShown(false), []);
    const { joinByInviteCode, isJoining } = useInviteCodeJoin();

    // The preview beside the list goes back to the selected crucible once the form closes.
    useEffect(() => {
        if (!isCreateFormShown) {
            onCreateDraftChange?.(null);
        }
    }, [onCreateDraftChange, isCreateFormShown]);

    if (isCreateFormShown) {
        return (
            <CreateCruciblePanel
                characterReadiness={characterReadiness}
                isSaturationLimited={isSaturationLimited}
                onDraftChange={onCreateDraftChange}
                onCancel={() => setIsCreateFormShown(false)}
                onCreated={(crucibleId) => {
                    setIsCreateFormShown(false);
                    onSelectCrucible(crucibleId);
                }}
            />
        );
    }

    return (
        <Panel className={styles.availablePanel}>
            <div className={styles.headerRow} {...toTourTargetProps('crucibles-actions')}>
                <h2 className={styles.heading}>Available Crucibles</h2>
                <span ref={joinViaCodeRef}>
                    <Button
                        size="small"
                        variant="link"
                        onClick={() => setIsJoinViaCodeShown((wasShown) => !wasShown)}
                        aria-expanded={isJoinViaCodeShown}
                        aria-haspopup="dialog"
                    >
                        Join via code
                    </Button>
                </span>
                <Button
                    size="small"
                    variant="secondary"
                    onClick={() => setIsCreateFormShown(true)}
                    className={styles.createButton}
                    title="Create a new crucible"
                >
                    + Create
                </Button>
            </div>

            <Popover open={isJoinViaCodeShown} anchorRef={joinViaCodeRef} placement="bottom" onClose={closeJoinViaCode}>
                <InviteCodeForm onJoin={joinByInviteCode} isJoining={isJoining} />
            </Popover>

            <ul className={styles.crucibleList} {...toTourTargetProps('crucibles-list')}>
                {crucibles.map((crucible) => (
                    <li key={crucible.id}>
                        <CrucibleCard
                            crucible={crucible}
                            isSelected={crucible.id === selectedCrucibleId}
                            onSelect={() => onSelectCrucible(crucible.id)}
                            characterReadiness={characterReadiness}
                        />
                    </li>
                ))}
            </ul>
            {hasMoreCrucibles && (
                <Button
                    size="small"
                    variant="secondary"
                    className={styles.showMoreButton}
                    onClick={onShowMoreCrucibles}
                    disabled={isLoadingMoreCrucibles}
                >
                    {isLoadingMoreCrucibles ? 'Loading...' : 'Show more'}
                </Button>
            )}
        </Panel>
    );
}
